// src/App.js
// --- UPDATE (toast once + SLA AI) ----------------------------------------------------
//   • NotificationProvider now runs on FlowSense Notify: every notification shows ONCE (one toast in
//     the visible tab, or one OS pop-up when FlowSense is in the background) — see
//     contexts/NotificationContext.js. Same useNotifications() API for every page.
//   • Firebase web token registration removed: browsers use FlowSense browser push (VAPID). Firebase
//     web + browser push showed every message twice. Browsers that had allowed notifications are
//     switched over automatically; the old Firebase web worker is unregistered.
//   • /admin pages honour route.role (e.g. SLA Command Center = supervision only), not just the sidebar.
//   • Page title also matches nested pages (/admin/workorders/123) and "/Issue Management".
// --- END UPDATE ----------------------------------------------------------------------
import React, { useState, useEffect } from 'react';
// Import routing components
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';

// Import Layouts and Protected Route component
import AdminNavbar from './components/navbar/AdminNavbar';
import Sidebar from './components/sidebar/Sidebar';
import AuthLayout from './layouts/auth/AuthLayout'; // Import Auth Layout
import ProtectedRoute from './components/auth/ProtectedRoute'; // Import Protected Route
import { NotificationProvider } from './contexts/NotificationContext';

// =========================================================================
// ENTERPRISE FIX: Import Global AI Insights Provider
// =========================================================================
import { AIInsightsProvider } from './contexts/AIInsightsContext';

// --- UPDATE: Firebase web registration removed (browsers use FlowSense browser push)
import { getUserInfo } from './utils/auth';

// import { config } from './config'; // Keep if needed for other global config
import routes, { canAccessRoute } from './routes'; // Import route configuration

// Import Chakra UI components
import { Box, Flex, Text, useDisclosure, useColorModeValue, Heading, Spinner, Center, Button, Icon, VStack } from '@chakra-ui/react';
import { MdLockOutline } from 'react-icons/md';

// 🔥 FIX: Import the official Google Maps loader to prevent race conditions
import { useJsApiLoader } from '@react-google-maps/api'; 

// Define the libraries array outside the component to prevent infinite re-renders
const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry', 'drawing'];

// --- UPDATE: route-level role check (route.role in routes.js — same rule as the sidebar)
const safeDecode = (s) => { try { return decodeURIComponent(s); } catch (_) { return s; } };
const currentUser = () => { try { return getUserInfo() || null; } catch (_) { return null; } };

function NoAccess({ name }) {
  const muted = useColorModeValue('gray.600', 'gray.400');
  return (
    <Center minH="50vh">
      <VStack spacing={3} textAlign="center" maxW="420px">
        <Icon as={MdLockOutline} boxSize={10} color="gray.400" />
        <Heading size="md">No access to {name}</Heading>
        <Text color={muted} fontSize="sm">Your role cannot open this page. Ask an administrator if you need it.</Text>
        <Button as="a" href="/admin/dashboard" size="sm" colorScheme="blue">Back to dashboard</Button>
      </VStack>
    </Center>
  );
}

function RouteGate({ route, children }) {
  // Without a known user the ProtectedRoute decides; only block when we KNOW the role is not allowed
  const user = currentUser();
  if (route.role && user?.role && !canAccessRoute(route, user)) return <NoAccess name={route.name} />;
  return children;
}
// --- END UPDATE

// Page components are imported within routes.js

function App() {

  // Function to generate routes based on layout
  const getRoutes = (layoutPath) => {
    return routes.map((prop, key) => {
      if (prop.layout === layoutPath) {
        const RouteComponent = prop.component;
        if (!RouteComponent) {
             console.warn(`Route component for path ${prop.layout}${prop.path} is null or undefined.`);
             return null; // Skip if component is missing
        }
        // *** FIX: Use relative path (prop.path) for nested routes ***
        // Remove the leading '/' if it exists in prop.path
        const relativePath = prop.path.startsWith('/') ? prop.path.substring(1) : prop.path;
        return (
          <Route
            path={relativePath} // Use relative path (e.g., "dashboard", "sign-in")
            element={layoutPath === '/admin'
              ? <RouteGate route={prop}><RouteComponent /></RouteGate> // --- UPDATE: role check
              : <RouteComponent />}
            key={key}
          />
        );
      } else {
        return null;
      }
    });
  };

  return (
    // =========================================================================
    // GLOBAL NOTIFICATION PROVIDER (FlowSense Notify)
    // One live connection for the whole app; each notification pops up once.
    // =========================================================================
    <NotificationProvider>
      {/* ========================================================================= */}
      {/* ENTERPRISE FIX: GLOBAL AI INSIGHTS PROVIDER                               */}
      {/* Wraps the routing tree to ensure O(1) Redis polling, RLHF feedback, and */}
      {/* AI WebSocket connections are initialized exactly once across the app.   */}
      {/* ========================================================================= */}
      <AIInsightsProvider>
        {/* Top-level Routes component manages rendering based on URL */}
        <Routes>
          {/* --- Authentication Routes --- */}
          {/* Parent route for /auth, renders AuthLayout */}
          <Route path="/auth" element={<AuthLayout />}>
            {/* Nested routes rendered inside AuthLayout's Outlet */}
            {getRoutes("/auth")}
            {/* Default redirect within /auth */}
            <Route index element={<Navigate to="/auth/sign-in" replace />} />
          </Route>

          {/* --- Admin/Dashboard Routes (Protected) --- */}
          {/* Parent route for /admin, protected by ProtectedRoute */}
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
             {/* Nested routes rendered inside AdminLayout's Outlet */}
             {getRoutes("/admin")}
             {/* Default redirect within /admin */}
             <Route index element={<Navigate to="/admin/dashboard" replace />} />
          </Route>

          {/* --- Default Redirect --- */}
          {/* Redirect from root '/' to admin dashboard */}
          <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />

          {/* --- Fallback 404 Route --- */}
          <Route path="*" element={<NotFound />} />

        </Routes>
      </AIInsightsProvider>
    </NotificationProvider>
  );
}

// --- Helper Component for the Main Admin Layout ---
function AdminLayout() {
    const { isOpen: isMobileNavOpen, onOpen: onMobileNavOpen, onClose: onMobileNavClose } = useDisclosure();
    const [currentBrandText, setCurrentBrandText] = useState("Dashboard");
    const sidebarWidth = "300px";
    const location = useLocation();
    const layoutBg = useColorModeValue("gray.50", "navy.900"); // --- UPDATE: hoisted
    const footerBorder = useColorModeValue("gray.200", "gray.700"); // --- UPDATE: hoisted

    // 🔥 FIX: Global Google Maps Loader tied to the Admin Layout
    // This blocks the Outlet (Dashboard/Maps) from rendering until the Google object is attached to the window
    const { isLoaded: isGoogleMapsLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
        libraries: GOOGLE_MAPS_LIBRARIES
    });

    useEffect(() => {
        // --- UPDATE: decode ("/Issue%20Management") and match nested pages ("/admin/workorders/123")
        const path = safeDecode(location.pathname);
        const currentRoute = routes
            .filter(r => r.layout === '/admin')
            .find(r => path === r.layout + r.path || path.startsWith(`${r.layout}${r.path}/`));
        if (currentRoute) {
            setCurrentBrandText(currentRoute.name);
        } else {
             // Handle cases where the path might be nested deeper than defined in routes.js
             // or set a default title
             setCurrentBrandText("FlowSense");
        }
    }, [location]);

    // --- UPDATE: the Firebase web token sync that lived here is gone. Browser notifications now come
    // from FlowSense browser push (VAPID, set up by NotificationProvider); with both, every message
    // popped up twice.

    return (
         <Flex direction="row" minH="100vh" bg={layoutBg}>
            <Sidebar routes={routes} />
            <Flex
                as="main" direction="column" flexGrow={1} minH="100vh"
                ml={{ base: "0", xl: sidebarWidth }} w={{ base: "100%", xl: `calc(100% - ${sidebarWidth})` }}
                transition="margin-left 0.2s linear"
            >
                <AdminNavbar
                    brandText={currentBrandText} secondary={false} message="" fixed={true}
                    onOpen={onMobileNavOpen}
                    w={{ base: 'calc(100vw - 6%)', md: 'calc(100vw - 8%)', lg: 'calc(100vw - 6%)',
                         xl: `calc(100vw - ${sidebarWidth} - 60px)`, '2xl': `calc(100vw - ${sidebarWidth} - 75px)` }}
                    isOpen={isMobileNavOpen} onClose={onMobileNavClose}
                />
                <Box flexGrow={1} p={{ base: 4, md: 6 }} mt="85px">
                    {/* 🔥 FIX: Ensure critical external scripts (Google Maps) are ready before rendering routes */}
                    {!isGoogleMapsLoaded && !loadError ? (
                        <Center h="50vh">
                            <Spinner size="xl" color="blue.500" thickness="4px" />
                        </Center>
                    ) : loadError ? (
                        <Center h="50vh">
                            <Text color="red.500" fontWeight="bold">Failed to load Map dependencies. Please check API Key.</Text>
                        </Center>
                    ) : (
                        /* Outlet renders the matched nested /admin/* page component */
                        <Outlet />
                    )}
                </Box>
                <Box as="footer" p={4} textAlign="center" borderTopWidth="1px" mt="auto" borderColor={footerBorder}>
                    <Text fontSize="sm" color="gray.500">© {new Date().getFullYear()} FlowSense Water Management</Text>
                </Box>
            </Flex>
        </Flex>
    );
}

// --- Simple 404 Component ---
function NotFound() {
    return (
        <Flex justify="center" align="center" height="100vh" direction="column">
            <Heading>404 - Not Found</Heading>
            <Text>Sorry, the page you are looking for does not exist.</Text>
        </Flex>
    );
}

export default App;